<template>
  <div>
    <v-divider class="my-4" />
    <div class="text-subtitle-1 font-weight-medium mb-2">Bankkonten</div>

    <div v-if="!loading && contractAccountRelations.length === 0" class="text--secondary text-body-2 mb-3">
      Keine Bankkonten verknüpft.
    </div>

    <v-list dense v-if="contractAccountRelations.length > 0" class="mb-2">
      <v-list-item
        v-for="relation in contractAccountRelations"
        :key="relation.id"
      >
        <v-list-item-content>
          <v-list-item-title>{{ accountLabel(relation.bank_account) }}</v-list-item-title>
          <v-list-item-subtitle>{{ usageLabel(relation.usage) }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="isSuperuser">
          <v-btn icon small @click="deleteRelation(relation)">
            <v-icon small>{{ mdiDelete }}</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <template v-if="isSuperuser">
      <div>
        <v-btn text small color="primary" @click="showAddRelation = !showAddRelation">
          <v-icon left small>{{ mdiLinkVariant }}</v-icon>
          Konto verknüpfen
        </v-btn>
        <v-btn text small color="primary" class="ml-2" @click="showAddAccount = !showAddAccount">
          <v-icon left small>{{ mdiPlus }}</v-icon>
          Neues Konto anlegen
        </v-btn>
      </div>

      <v-card v-if="showAddRelation" outlined class="mt-2 mb-2">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="7">
              <v-autocomplete
                v-model="newRelation.bankAccountUrl"
                :items="availableAccounts"
                item-value="url"
                :item-text="accountItemText"
                label="Bankkonto"
                dense
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-select
                v-model="newRelation.usage"
                :items="usageOptions"
                label="Verwendung"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAddRelation = false">Abbrechen</v-btn>
          <v-btn text color="primary" :disabled="!newRelation.bankAccountUrl" @click="saveRelation">Speichern</v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="showAddAccount" outlined class="mt-2 mb-2">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="5">
              <v-text-field v-model="newAccount.iban" label="IBAN" dense :rules="ibanRules" />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="newAccount.bic" label="BIC" dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="newAccount.description" label="Beschreibung" dense />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showAddAccount = false">Abbrechen</v-btn>
          <v-btn text color="primary" :disabled="!newAccount.iban" @click="createAccount">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </div>
</template>

<script>
import { mdiDelete, mdiLinkVariant, mdiPlus } from '@mdi/js'
import { mapGetters } from 'vuex'
import { electronicFormatIBAN, isValidIBAN } from 'ibantools'
import { SET_ERROR_MESSAGE, SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'

const USAGE_LABELS = {
  collection_target: 'Geldeingang Sammlung',
  billing_source: 'Geldabgang Lastschrift'
}

export default {
  name: 'ContractBankAccountsSection',
  props: {
    contract: { type: Object, required: true },
    organization: { type: Object, required: true }
  },
  data () {
    return {
      mdiDelete,
      mdiLinkVariant,
      mdiPlus,
      loading: false,
      contractAccountRelations: [],
      availableAccounts: [],
      accountsByUrl: {},
      showAddRelation: false,
      showAddAccount: false,
      newRelation: { bankAccountUrl: null, usage: 'collection_target' },
      newAccount: { iban: '', bic: '', description: '' },
      usageOptions: [
        { value: 'collection_target', text: USAGE_LABELS.collection_target },
        { value: 'billing_source', text: USAGE_LABELS.billing_source }
      ],
      ibanRules: [
        value => !value || isValidIBAN(electronicFormatIBAN(value || '')) || 'Bitte geben Sie eine gültige IBAN ein.'
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['isSuperuser'])
  },
  methods: {
    usageLabel (usage) {
      return USAGE_LABELS[usage] || usage
    },
    accountLabel (bankAccountUrl) {
      const account = this.accountsByUrl[bankAccountUrl]
      if (!account) return bankAccountUrl
      return account.description ? `${account.description} (${account.iban})` : account.iban
    },
    accountItemText (account) {
      return account.description ? `${account.description} (${account.iban})` : account.iban
    },
    async fetchRelations () {
      this.loading = true
      try {
        const response = await this.$store.getters.restApi.get(`/contract/${this.contract.id}/account_relation/`)
        this.contractAccountRelations = response.data
        this.$emit('relations-changed', this.contractAccountRelations)
      } finally {
        this.loading = false
      }
    },
    async fetchAccounts () {
      const response = await this.$store.getters.restApi.get('/account/')
      this.availableAccounts = response.data
      this.accountsByUrl = Object.fromEntries(response.data.map(a => [a.url, a]))
    },
    async deleteRelation (relation) {
      try {
        await this.$store.getters.restApi.delete(relation.url)
        this.contractAccountRelations = this.contractAccountRelations.filter(r => r.id !== relation.id)
        this.$emit('relations-changed', this.contractAccountRelations)
        this.$store.commit(SET_SNACKBAR_MESSAGE, { message: 'Bankkontoverbindung entfernt', color: 'success' })
      } catch {
        this.$store.commit(SET_ERROR_MESSAGE, { message: 'Fehler beim Entfernen der Bankkontoverbindung' })
      }
    },
    async saveRelation () {
      try {
        const response = await this.$store.getters.restApi.post(`/contract/${this.contract.id}/account_relation/`, {
          contract: this.contract.url,
          bank_account: this.newRelation.bankAccountUrl,
          usage: this.newRelation.usage
        })
        this.contractAccountRelations.push(response.data)
        this.$emit('relations-changed', this.contractAccountRelations)
        this.showAddRelation = false
        this.newRelation = { bankAccountUrl: null, usage: 'collection_target' }
        this.$store.commit(SET_SNACKBAR_MESSAGE, { message: 'Bankkonto verknüpft', color: 'success' })
      } catch {
        this.$store.commit(SET_ERROR_MESSAGE, { message: 'Fehler beim Verknüpfen des Bankkontos' })
      }
    },
    async createAccount () {
      try {
        const payload = {
          iban: electronicFormatIBAN(this.newAccount.iban),
          owner: this.organization.url
        }
        if (this.newAccount.bic) payload.bic = this.newAccount.bic
        if (this.newAccount.description) payload.description = this.newAccount.description

        const response = await this.$store.getters.restApi.post('/account/', payload)
        this.availableAccounts.push(response.data)
        this.accountsByUrl[response.data.url] = response.data
        this.showAddAccount = false
        this.newAccount = { iban: '', bic: '', description: '' }
        this.$store.commit(SET_SNACKBAR_MESSAGE, { message: 'Bankkonto angelegt', color: 'success' })
      } catch (error) {
        const msg = error?.response?.data?.iban?.[0] || error?.response?.data?.detail || 'Fehler beim Anlegen des Bankkontos'
        this.$store.commit(SET_ERROR_MESSAGE, { message: msg })
      }
    }
  },
  watch: {
    contract: {
      immediate: true,
      async handler () {
        await this.fetchRelations()
        if (this.isSuperuser) {
          await this.fetchAccounts()
        }
      }
    }
  }
}
</script>
